import { jsPDF } from 'jspdf';
import { FORCAS_DIAGNOSTICO } from './index.js';
import { buildRadarSvgInner } from './radar.js';
import { calcScoreTema, lnv, nv } from '@shared/scores.js';

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** A cor de cada força: `barra` em faixas e barras, `texto` (escurecida) em letras. */
const CORES = FORCAS_DIAGNOSTICO.map((f) => ({ barra: rgb(f.hex), texto: rgb(f.texto) }));

export async function downloadDiagnosticoPdf(sc: number[], flat: number[]): Promise<void> {
  const F = FORCAS_DIAGNOSTICO;
  const med = sc.reduce((a, b) => a + b, 0) / 5;

  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svgEl.setAttribute('viewBox', '0 0 500 500');
  svgEl.innerHTML = buildRadarSvgInner(sc);
  const svgData = new XMLSerializer().serializeToString(svgEl);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const radarImg: string | null = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(svgUrl);
        resolve(null);
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      URL.revokeObjectURL(svgUrl);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      resolve(null);
    };
    img.src = svgUrl;
  });

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 16;
  let y = M;
  // Fundo branco, texto preto; o amarelo vai em faixas e barras, nunca em letra.
  // A fonte do PDF (helvetica) não tem emoji nem seta: no texto, só Latin-1.
  const AM = [255, 200, 0];
  const PR = [10, 10, 10];
  const CZ = [92, 92, 92];
  const G1 = [245, 245, 245];

  doc.setFillColor(G1[0]!, G1[1]!, G1[2]!);
  doc.rect(0, 0, W, 46, 'F');
  doc.setFillColor(AM[0]!, AM[1]!, AM[2]!);
  doc.rect(0, 44.5, W, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(CZ[0]!, CZ[1]!, CZ[2]!);
  doc.text('HACK ACADEMY · ESCOLA DE NEGÓCIOS PARA EMPRESÁRIOS E LÍDERES', W / 2, 10, { align: 'center' });
  doc.setFontSize(18);
  doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
  doc.text('As 5 Forças do Crescimento', W / 2, 22, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
  doc.text('Diagnóstico de Maturidade Empresarial', W / 2, 31, { align: 'center' });
  doc.setFontSize(7);
  doc.setTextColor(CZ[0]!, CZ[1]!, CZ[2]!);
  const hoje = new Date();
  doc.text(`Gerado em ${hoje.toLocaleDateString('pt-BR')} · Hack Academy`, W / 2, 39, { align: 'center' });
  y = 52;

  doc.setFillColor(G1[0]!, G1[1]!, G1[2]!);
  doc.rect(M, y, W - M * 2, 22, 'F');
  doc.setFillColor(AM[0]!, AM[1]!, AM[2]!);
  doc.rect(M, y, 3, 22, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(CZ[0]!, CZ[1]!, CZ[2]!);
  doc.text('MÉDIA GERAL', M + 7, y + 8);
  doc.setFontSize(20);
  doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
  doc.text(`${med.toFixed(1).replace('.', ',')} / 10`, M + 7, y + 19);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(CZ[0]!, CZ[1]!, CZ[2]!);
  doc.text(lnv(med), W - M - 3, y + 13, { align: 'right' });
  y += 28;

  if (radarImg) {
    const rW = 70;
    const rH = 70;
    doc.addImage(radarImg, 'PNG', (W - rW) / 2, y + 2, rW, rH);
    y += rH + 8;
  }

  F.forEach((f, i) => {
    const s = sc[i]!;
    const c = CORES[i]!;
    const pct = s / 10;
    const bW = W - M * 2 - 8;
    doc.setFillColor(G1[0]!, G1[1]!, G1[2]!);
    doc.rect(M, y, W - M * 2, 15, 'F');
    doc.setFillColor(c.barra[0], c.barra[1], c.barra[2]);
    doc.rect(M, y, 3, 15, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(c.texto[0], c.texto[1], c.texto[2]);
    doc.text(
      `${f.num} ${f.nome.toUpperCase()} · ${f.temas[0]!.nome} & ${f.temas[1]!.nome}`,
      M + 6,
      y + 6,
    );
    doc.setFontSize(10);
    doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
    doc.text(`${s.toFixed(1).replace('.', ',')}/10`, W - M - 3, y + 7, { align: 'right' });
    doc.setFillColor(220, 220, 220);
    doc.rect(M + 6, y + 9.5, bW, 2.5, 'F');
    doc.setFillColor(c.barra[0], c.barra[1], c.barra[2]);
    doc.rect(M + 6, y + 9.5, bW * pct, 2.5, 'F');
    const sA = calcScoreTema(flat, i, 0);
    const sB = calcScoreTema(flat, i, 1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(CZ[0]!, CZ[1]!, CZ[2]!);
    doc.text(
      `${f.temas[0]!.nome}: ${sA.toFixed(1).replace('.', ',')} | ${f.temas[1]!.nome}: ${sB.toFixed(1).replace('.', ',')}`,
      M + 6,
      y + 14,
    );
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(c.texto[0], c.texto[1], c.texto[2]);
    doc.text(lnv(s), W - M - 3, y + 14, { align: 'right' });
    y += 17;
  });
  y += 4;

  const ord = F.map((f, i) => ({ f, i, s: sc[i]! })).sort((a, b) => a.s - b.s);
  ord.forEach((o) => {
    const f = o.f;
    const fi = o.i;
    const s = o.s;
    const n = nv(s);
    const nk = n as 'alto' | 'medio' | 'baixo';
    const c = CORES[fi]!;
    if (y > 240) {
      doc.addPage();
      y = M;
    }
    doc.setFillColor(G1[0]!, G1[1]!, G1[2]!);
    doc.rect(M, y, W - M * 2, 7, 'F');
    doc.setFillColor(c.barra[0], c.barra[1], c.barra[2]);
    doc.rect(M, y, W - M * 2, 0.8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(c.texto[0], c.texto[1], c.texto[2]);
    doc.text(
      `${f.num} ${f.nome.toUpperCase()} — ${f.temas[0]!.nome} & ${f.temas[1]!.nome}`,
      M + 4,
      y + 5,
    );
    doc.setFontSize(8);
    doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
    doc.text(`${s.toFixed(1).replace('.', ',')}/10 · ${lnv(s)}`, W - M - 3, y + 5, { align: 'right' });
    y += 9;
    if (y > 270) {
      doc.addPage();
      y = M;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(64, 64, 64);
    const lines = doc.splitTextToSize(f.niveis[nk], W - M * 2 - 6);
    doc.text(lines, M + 4, y);
    y += lines.length * 3.5 + 3;
    if (y > 265) {
      doc.addPage();
      y = M;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(c.texto[0], c.texto[1], c.texto[2]);
    doc.text('O QUE PRIORIZAR:', M + 4, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
    f.prio[nk].forEach((p: string) => {
      if (y > 272) {
        doc.addPage();
        y = M;
      }
      const pl = doc.splitTextToSize(`- ${p}`, W - M * 2 - 10);
      doc.text(pl, M + 6, y);
      y += pl.length * 3.5 + 2;
    });
    y += 4;
  });

  doc.setFillColor(G1[0]!, G1[1]!, G1[2]!);
  doc.rect(0, 280, W, 17, 'F');
  doc.setFillColor(AM[0]!, AM[1]!, AM[2]!);
  doc.rect(0, 280, W, 0.7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(PR[0]!, PR[1]!, PR[2]!);
  doc.text('Hack Academy · academy.hack.net.br', W / 2, 288, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(CZ[0]!, CZ[1]!, CZ[2]!);
  doc.text('As 5 Forças do Crescimento · Hack Sales Performance Ltda.', W / 2, 293, { align: 'center' });

  doc.save('diagnostico-5-forcas-hack-academy.pdf');
}
